import { useCallback } from 'react';
import { useEffectOnce } from '@beeline.com/bp-design-system';
import { gql, useLazyQuery } from '@apollo/client';
import { FETCH_POLICY } from 'common/apollo/FETCH_POLICY';
import { TerminationReasonsModel } from '@utmost.co/enterprise-models';
import { PageModelInput } from '@utmost.co/enterprise-models';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { useTerminationReasonsIntl } from '../translations/useTerminationReasonsIntl';

export const GET_REASONS_QUERY = gql`
  query TerminationReasons($page: PageModelInput) {
    terminationReasons(page: $page) {
      limit
      offset
      totalCount
      items {
        assignable
        eligibleForRehire
        name
        uid
        status
        sourceName
        referenceId
      }
    }
  }
`;

export function useTerminationReasons(skipQuery?: boolean) {
  const { showError } = useSnackbar();
  const intl = useTerminationReasonsIntl();
  const [getTerminationReasons, { data, loading }] = useLazyQuery<
    { terminationReasons: TerminationReasonsModel },
    { page: PageModelInput }
  >(GET_REASONS_QUERY, {
    fetchPolicy: FETCH_POLICY.NETWORK_ONLY,
    onError() {
      showError(intl.error);
    }
  });

  const fetchTerminationReasons = useCallback(
    (offset: number) => {
      const limit = 30;
      if (!skipQuery) {
        getTerminationReasons({
          variables: {
            page: { offset: offset * limit, limit }
          }
        });
      }
    },
    [getTerminationReasons, skipQuery]
  );

  useEffectOnce(() => {
    fetchTerminationReasons(0);
  });

  return {
    data,
    loading,
    fetchTerminationReasons
  };
}
